import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  UserCircle, ArrowLeft, Clock, PencilSimple, CalendarBlank,
  Coffee, SignIn, SignOut, Hourglass,
} from "@phosphor-icons/react";

function fmtMs(ms) {
  if (!ms || ms < 0) return "0h 0m";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
}
function fmtTs(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function OfficeStaffProfile() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [reportDate, setReportDate] = useState(todayISO());
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, b, c, d] = await Promise.all([
          api.get(`/office-staff/${id}`),
          api.get(`/office-staff/${id}/sessions?limit=15`),
          api.get(`/office-staff/${id}/activity-logs?limit=60`),
          api.get(`/office-staff/${id}/payroll`),
        ]);
        setS(a.data); setSessions(b.data); setLogs(c.data); setPayroll(d.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setReportLoading(true);
    api.get(`/office-staff/${id}/daily-report?date=${reportDate}`)
      .then(r => setReport(r.data))
      .catch(() => setReport(null))
      .finally(() => setReportLoading(false));
  }, [id, reportDate]);

  if (!s) return <AppShell title="Staff Profile"><div className="text-sm text-[#8A8780]">Loading…</div></AppShell>;

  return (
    <AppShell title={s.name} subtitle={`Office staff · ${s.staff_code || ""}`}
      actions={
        <div className="flex gap-2">
          <Link to={`/admin/staff-mgmt/${id}/edit`} className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
            <PencilSimple size={14}/>Edit
          </Link>
          <Link to="/admin/staff-mgmt" className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
            <ArrowLeft size={14}/>Back
          </Link>
        </div>
      }>
      <div className="grid grid-cols-12 gap-5" data-testid="office-profile">
        <div className="col-span-12 md:col-span-4 bg-white border border-[#E5E2D9] rounded-md p-5">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 rounded-full bg-[#D4E0D1] flex items-center justify-center">
              <UserCircle size={32} color="#2D5A27" weight="duotone"/>
            </div>
            <div>
              <div className="font-heading text-xl leading-tight">{s.name}</div>
              <div className="text-xs text-[#5C5A55] capitalize mt-1">{s.role}</div>
              <div className="text-[11px] font-mono text-[#8A8780] mt-0.5">{s.staff_code || "—"}</div>
            </div>
          </div>
          <div className="mt-5 space-y-2 text-sm">
            <Row k="Email" v={s.email}/>
            <Row k="Phone" v={s.phone || "—"}/>
            <Row k="Status" v={s.status || "active"}/>
            <Row k="Hourly Rate" v={`₹${s.hourly_rate || 0}/hr`}/>
            <Row k="Base Salary" v={`₹${s.base_salary || 0}/mo`}/>
            <Row k="Daily Hours" v={s.daily_hours ? `${s.daily_hours} h/day` : "tenant default"}/>
            <Row k="Joined" v={s.date_of_joining || "—"}/>
            <Row k="Emergency" v={s.emergency_contact || "—"}/>
          </div>
        </div>

        <div className="col-span-12 md:col-span-8 space-y-5">
          {payroll && (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="payroll-card">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] flex items-center gap-1"><Clock size={11}/>Current month ({payroll.month})</div>
              <div className="grid grid-cols-4 gap-4 mt-3">
                <Kpi label="Active" v={`${payroll.active_hours}h`}/>
                <Kpi label="Break" v={`${payroll.break_hours}h`}/>
                <Kpi label="Idle" v={`${payroll.inactive_hours}h`}/>
                <Kpi label="Gross" v={`₹${Number(payroll.gross).toFixed(0)}`}/>
              </div>
              <div className="text-[11px] text-[#8A8780] mt-3">
                {payroll.days_worked} days · expected {payroll.daily_hours_expected}h/day · OT {payroll.ot_hours}h · UT {payroll.ut_hours}h
              </div>
            </div>
          )}

          {/* Daily Activity Report */}
          <div className="bg-white border border-[#E5E2D9] rounded-md" data-testid="daily-report-card">
            <div className="px-5 py-3 border-b border-[#E5E2D9] flex items-center gap-3 flex-wrap">
              <CalendarBlank size={14} color="#2D5A27"/>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Daily Activity Report</div>
              <div className="flex-1"/>
              <input type="date" value={reportDate}
                      max={todayISO()}
                      onChange={e => setReportDate(e.target.value)}
                      data-testid="daily-report-date"
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            </div>
            {reportLoading && <div className="px-5 py-8 text-center text-xs text-[#8A8780]">Loading…</div>}
            {!reportLoading && report && (
              <div className="p-5 space-y-5">
                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <ReportKpi label="First login"  Icon={SignIn}    color="#2D5A27" v={fmtTs(report.first_login)}/>
                  <ReportKpi label="Last logout"  Icon={SignOut}   color="#B12A1E" v={report.session_open ? "● live" : fmtTs(report.last_logout)}/>
                  <ReportKpi label="Active"       Icon={Clock}     color="#2D5A27" v={fmtMs(report.total_active_ms)}/>
                  <ReportKpi label="On break"     Icon={Coffee}    color="#C9A227" v={fmtMs(report.total_break_ms)}/>
                  <ReportKpi label="Idle"         Icon={Hourglass} color="#5C5A55" v={fmtMs(report.total_inactive_ms)}/>
                </div>
                {/* Counts */}
                <div className="text-[11px] text-[#8A8780] flex flex-wrap gap-4">
                  <span><b className="text-[#1A1A1A]">{report.session_count}</b> session{report.session_count !== 1 ? "s" : ""}</span>
                  <span><b className="text-[#1A1A1A]">{report.break_count}</b> break{report.break_count !== 1 ? "s" : ""} · {report.manual_break_count} manual · {report.auto_break_count} auto</span>
                </div>
                {/* Event timeline */}
                {report.events.length > 0 ? (
                  <div className="border-t border-[#F1EFE7] pt-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Event timeline</div>
                    <ul className="space-y-1.5">
                      {report.events.map((e, i) => (
                        <li key={i} className="flex items-center gap-3 text-xs"
                              data-testid={`report-event-${i}`}>
                          <div className="font-mono text-[11px] text-[#5C5A55] w-14">{fmtTs(e.ts)}</div>
                          <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold
                            ${e.type === "login" ? "bg-emerald-100 text-emerald-800"
                            : e.type === "logout" ? "bg-rose-100 text-rose-800"
                            : e.type === "break_start" ? "bg-amber-100 text-amber-900"
                            : "bg-blue-100 text-blue-900"}`}>
                            {e.type.replace("_", " ")}
                            {e.kind === "auto" ? " · auto" : ""}
                          </span>
                          {e.duration_ms ? (
                            <span className="text-[11px] text-[#8A8780] font-mono">{fmtMs(e.duration_ms)}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-center text-xs text-[#8A8780] italic py-4">
                    No activity recorded for {reportDate}.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-md">
            <div className="px-5 py-3 border-b border-[#E5E2D9] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Duty Sessions</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#8A8780] bg-[#FAFAF7]">
                  <th className="px-5 py-2">Start</th>
                  <th className="px-5 py-2">End</th>
                  <th className="px-5 py-2 text-right">Active</th>
                  <th className="px-5 py-2 text-right">Break</th>
                  <th className="px-5 py-2 text-right">Idle</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-center text-[#8A8780]">No sessions.</td></tr>}
                {sessions.map(ss => (
                  <tr key={ss.id} className="border-t border-[#E5E2D9]">
                    <td className="px-5 py-2 font-mono text-xs">{new Date(ss.start_time).toLocaleString()}</td>
                    <td className="px-5 py-2 font-mono text-xs">{ss.end_time ? new Date(ss.end_time).toLocaleString() : <span className="text-[#2D5A27]">● live</span>}</td>
                    <td className="px-5 py-2 text-right font-mono">{fmtMs(ss.active_ms)}</td>
                    <td className="px-5 py-2 text-right font-mono">{fmtMs(ss.break_ms)}</td>
                    <td className="px-5 py-2 text-right font-mono">{fmtMs(ss.inactive_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-[#E5E2D9] rounded-md">
            <div className="px-5 py-3 border-b border-[#E5E2D9] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Activity Logs</div>
            <div className="max-h-96 overflow-y-auto">
              {logs.length === 0 && <div className="px-5 py-6 text-xs text-[#8A8780]">No activity.</div>}
              {logs.map(l => (
                <div key={l.id} className="px-5 py-2 border-t border-[#E5E2D9] flex items-center justify-between">
                  <div>
                    <div className="text-xs font-mono text-[#2D5A27]">{l.action}</div>
                    {l.target && <div className="text-[10px] text-[#8A8780] font-mono break-all">{l.target}</div>}
                  </div>
                  <div className="text-[10px] text-[#8A8780]">{new Date(l.timestamp).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between">
    <span className="text-[11px] uppercase tracking-[0.18em] text-[#8A8780]">{k}</span>
    <span className="font-mono text-xs text-[#2D2B26]">{v}</span>
  </div>
);
const Kpi = ({ label, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
    <div className="font-heading text-2xl leading-tight">{v}</div>
  </div>
);

function ReportKpi({ label, v, Icon, color }) {
  return (
    <div className="rounded-md border border-[#E5E2D9] p-3 bg-[#FAFAF7]">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
        <Icon size={10} weight="duotone" style={{ color }}/> {label}
      </div>
      <div className="font-heading text-lg mt-1" style={{ color }}>{v}</div>
    </div>
  );
}
